/**
 * TLAM Session Service
 *
 * Thin client for /api/tlam/sessions endpoints. The backend owns modem
 * selection (same `QueueService.findFreeModem` path scheduled flows use);
 * the client just names the location and org, then keeps the session alive.
 */

import { auth } from '@/config/firebase';
import type { ForcedRat } from '@simcheck/shared-types';
import { API_URL } from '@/config/env';

const API_BASE_URL = API_URL;

export interface TlamSessionStartResponse {
  sessionId: string;
  allocationId: string;
  lockId: string;
  deviceId: string;
  modemImei: string;
  modemModel?: string;
  eSimProfileId: string | null;
  /**
   * The APN this session's bearer uses: the caller's explicit override, or
   * the backend-resolved eSIM value (profile.apn → group.apn → network
   * defaultApn) — null when nothing is configured anywhere (issue #161).
   * The modal adopts this when it didn't supply an APN itself.
   */
  apn?: string | null;
  expiresAt: string; // ISO timestamp
}

async function getToken(): Promise<string> {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  return user.getIdToken();
}

export async function startTlamSession(params: {
  locationId: string;
  organizationId: string;
  eSimProfileId?: string;
  /**
   * eSIM *group* selected in the Quick Tests wizard. When set (and no specific
   * eSimProfileId is given), the backend resolves the best group member for the
   * modem it allocates — reusing the profile already installed on that modem
   * when present, otherwise installing an available one. This is the correct
   * path for group-based TLAM runs: the client can't know which modem will be
   * allocated, so it can't pick the right member up-front.
   */
  eSimGroupId?: string;
  /**
   * Pre-known APN (from localStorage). When provided, the backend kicks off
   * a `data_plane_prepare` on the edge device in the background so the
   * cellular bearer is warm by the time the user clicks Go. The response
   * still returns immediately — prepare runs in parallel.
   */
  apn?: string;
  /**
   * Force the modem to register on a specific PLMN (MCCMNC, 5–6 digits).
   * When set, the backend runs AT+COPS=1,2,"<MCCMNC>" on the allocated modem
   * and verifies registration before the session is created. If the modem
   * can't attach to the target network, the session is hard-failed — no
   * silent fallback to whatever PLMN the modem happens to find.
   */
  forcedNetworkMccMnc?: string;
  /**
   * Force the modem to attach on a specific Radio Access Technology.
   * When set, the backend filters allocation to capable modems only, then
   * dispatches a vendor-specific RAT-pin command (`AT+QCFG="nwscanmode"` /
   * `iotopmode` for EG25/EM120R/BG95, `AT+QNWPREFCFG="mode_pref"` for
   * RM520N) to the Pi. Hard-fails the session on any lock error. RAT-lock
   * runs *before* PLMN-lock so the PLMN attach happens on the chosen RAT.
   */
  forcedRat?: ForcedRat;
}): Promise<TlamSessionStartResponse> {
  const t0 = Date.now();
  console.log(
    `[lock:web] startTlamSession START locationId=${params.locationId} ` +
    `org=${params.organizationId} forcedRat=${params.forcedRat || '-'} ` +
    `forcedMccMnc=${params.forcedNetworkMccMnc || '-'}`
  );
  const token = await getToken();
  const res = await fetch(`${API_BASE_URL}/tlam/sessions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(params),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    console.warn(
      `[lock:web] startTlamSession FAILED status=${res.status} reason="${err.reason || err.message || ''}" ` +
      `ms=${Date.now() - t0}`
    );
    throw new Error(err.message || `Failed to start TLAM session: HTTP ${res.status}`);
  }
  const body = await res.json();
  console.log(
    `[lock:web] startTlamSession DONE sessionId=${body.sessionId} lockId=${body.lockId} ` +
    `imei=${body.modemImei} deviceId=${body.deviceId} ms=${Date.now() - t0}`
  );
  return body;
}

/**
 * Warm the cellular bearer on the edge device with the given APN. Used when
 * the user enters an APN via the browser's FlowInputsDialog (or changes it
 * mid-session). Fire-and-forget — the caller doesn't have to await, because
 * the edge's per-IMEI lock will serialize any subsequent `http_fetch` against
 * the in-flight prepare.
 *
 * Awaiting is still useful if you want to surface prepare errors to the user
 * before they click Go.
 */
export async function prepareTlamSession(
  sessionId: string,
  allocationId: string,
  apn: string,
): Promise<{ piDurationMs?: number; totalDurationMs?: number; namespace?: string | null }> {
  const token = await getToken();
  const res = await fetch(`${API_BASE_URL}/tlam/sessions/${sessionId}/prepare`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ allocationId, apn }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `Prepare failed: HTTP ${res.status}`);
  }
  return res.json();
}

export async function heartbeatTlamSession(
  sessionId: string,
  allocationId: string,
  lockId: string
): Promise<{
  sessionId: string;
  expiresAt: string;
  tokenBalance?: number;
  tokenWarning?: 'low_balance' | null;
  endSession?: boolean;
}> {
  const token = await getToken();
  const res = await fetch(`${API_BASE_URL}/tlam/sessions/${sessionId}/heartbeat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ allocationId, lockId }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `Heartbeat failed: HTTP ${res.status}`);
  }
  return res.json();
}

export async function endTlamSession(
  sessionId: string,
  allocationId: string,
  lockId: string,
  organizationId?: string
): Promise<void> {
  const t0 = Date.now();
  console.log(
    `[release:web] endTlamSession START sessionId=${sessionId} lockId=${lockId} ` +
    `allocationId=${allocationId}`
  );
  const token = await getToken();
  const res = await fetch(`${API_BASE_URL}/tlam/sessions/${sessionId}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ allocationId, lockId, organizationId }),
  });
  console.log(
    `[release:web] endTlamSession DONE sessionId=${sessionId} status=${res.status} ` +
    `ms=${Date.now() - t0}`
  );
}

/**
 * Best-effort release on tab close / reload. Uses `fetch(..., keepalive: true)`
 * so the request survives page teardown. Caller supplies a pre-fetched token
 * because we can't await inside a `pagehide` handler.
 */
export function endTlamSessionBeacon(
  sessionId: string,
  allocationId: string,
  lockId: string,
  token: string,
  organizationId?: string
): void {
  try {
    fetch(`${API_BASE_URL}/tlam/sessions/${sessionId}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ allocationId, lockId, organizationId }),
      keepalive: true,
    }).catch(() => { /* best effort */ });
  } catch { /* best effort */ }
}
